import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Loader2,
  CheckCircle2,
  Newspaper,
  AlertTriangle,
  ChevronDown,
  Lock,
  Send,
} from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { submitContact } from "@/server/contact.functions";
import { brandedTitle, safeDesc } from "@/lib/seo-meta";

const SITE = "https://heallexa.com";
const PHONE = "+1 (470) 800-0081";
const PHONE_HREF = "+14708000081";
const EMAIL = "contact@heallexa.com";

const META_TITLE = brandedTitle("Contact Us");
const META_DESC = safeDesc(
  "Get in touch with the Heallexa team. Call +1 (470) 800-0081, email contact@heallexa.com, or send us a message — we reply within 1–2 business days."
);

const TOPICS = [
  "General inquiry",
  "Claim or correct a listing",
  "Billing & subscriptions",
  "Press & media",
  "Privacy or data request",
  "Report a problem",
];

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: META_TITLE },
      { name: "description", content: META_DESC },
      { property: "og:title", content: META_TITLE },
      { property: "og:description", content: META_DESC },
      { property: "og:url", content: `${SITE}/contact` },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: META_TITLE },
      { name: "twitter:description", content: META_DESC },
    ],
    links: [{ rel: "canonical", href: `${SITE}/contact` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "@id": `${SITE}/contact#webpage`,
          url: `${SITE}/contact`,
          name: META_TITLE,
          description: META_DESC,
          isPartOf: { "@id": `${SITE}/#website` },
          about: { "@id": `${SITE}/#organization` },
        }),
      },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero band — soft primary wash fading into the page */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(70%_100%_at_50%_0%,color-mix(in_oklab,var(--color-primary)_14%,transparent),transparent_70%)]"
        />
        <div className="relative mx-auto max-w-[1100px] px-6 pt-32 md:pt-40 pb-14 md:pb-20 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card/70 px-3 py-1 text-[12px] font-medium text-muted-foreground backdrop-blur">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
            </span>
            Replies within 1–2 business days
          </span>

          <h1 className="mt-6 text-[2.6rem] md:text-[3.4rem] font-medium tracking-[-0.03em] leading-[1.05]">
            Let&apos;s get you an answer
          </h1>
          <p className="mx-auto mt-4 max-w-[560px] text-[16px] leading-relaxed text-muted-foreground">
            Questions about a listing, your subscription, or your data? Talk to a real person on the
            Heallexa team — Monday through Friday, 9 AM – 6 PM CT.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button asChild size="lg">
              <a href={`tel:${PHONE_HREF}`}>
                <Phone className="h-4 w-4" /> {PHONE}
              </a>
            </Button>
            <Button asChild size="lg" variant="outline">
              <a href={`mailto:${EMAIL}`}>
                <Mail className="h-4 w-4" /> {EMAIL}
              </a>
            </Button>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-[1100px] px-6 py-16 sm:py-20">
        {/* Form first on mobile — it's the point of the page; the hero already
            carries tap-to-call/email. Sidebar returns to the left on desktop. */}
        <div className="grid gap-10 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-14">
          <div className="order-1 lg:order-2">
            <ContactForm />
          </div>
          <div className="order-2 lg:order-1 lg:sticky lg:top-28 lg:self-start">
            <ContactDetails />
          </div>
        </div>
      </main>
      <FooterSection />
    </div>
  );
}

function ContactDetails() {
  return (
    <div className="space-y-4">
      <Detail icon={Phone} label="Phone" href={`tel:${PHONE_HREF}`} value={PHONE}>
        Mon – Fri, 9 AM – 6 PM CT
      </Detail>

      <Detail icon={Mail} label="General inquiries" href={`mailto:${EMAIL}`} value={EMAIL}>
        Response within 1–2 business days
      </Detail>

      <Detail icon={Newspaper} label="Press &amp; media" href={`mailto:${EMAIL}`} value={EMAIL}>
        Journalists: include your outlet and deadline
      </Detail>

      <Detail icon={MapPin} label="Mailing address" value="Heallexa Inc.">
        2351 W Northwest Hwy, Suite 1234
        <br />
        Dallas, TX 75220
      </Detail>

      <Detail icon={Clock} label="Business hours" value="Mon – Fri, 9 AM – 6 PM CT">
        Closed on U.S. federal holidays
      </Detail>

      <div className="rounded-lg border border-destructive/25 bg-destructive/[0.04] p-4 text-[13px] leading-relaxed space-y-2">
        <p className="flex items-center gap-2 font-medium text-foreground">
          <AlertTriangle className="h-4 w-4 text-destructive" aria-hidden="true" />
          Medical emergency? Call 911.
        </p>
        <p className="text-muted-foreground">
          Heallexa is a directory, not a healthcare provider — we can&apos;t give medical advice,
          diagnosis, or treatment. See our{" "}
          <Link to="/terms" className="underline underline-offset-2 hover:text-foreground">
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link to="/privacy" className="underline underline-offset-2 hover:text-foreground">
            Privacy Policy
          </Link>
          .
        </p>
      </div>
    </div>
  );
}

function Detail({
  icon: Icon,
  label,
  value,
  href,
  children,
}: {
  icon: typeof Phone;
  label: string;
  value: string;
  href?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="card-surface-flat hover-lift group flex gap-4 p-4">
      <span className="grid h-9 w-9 shrink-0 place-content-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary/15">
        <Icon className="h-4 w-4" aria-hidden="true" />
      </span>
      <div className="min-w-0 space-y-0.5">
        <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
        {href ? (
          <a href={href} className="block text-[15px] font-medium break-words hover:text-primary transition-colors">
            {value}
          </a>
        ) : (
          <p className="text-[15px] font-medium">{value}</p>
        )}
        <p className="text-[13px] leading-relaxed text-muted-foreground">{children}</p>
      </div>
    </div>
  );
}

function ContactForm() {
  const send = useServerFn(submitContact);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [message, setMessage] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [smsTransactional, setSmsTransactional] = useState(false);
  const [smsMarketing, setSmsMarketing] = useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    setSending(true);
    try {
      await send({
        data: {
          firstName: String(fd.get("firstName") ?? ""),
          lastName: String(fd.get("lastName") ?? ""),
          email: String(fd.get("email") ?? ""),
          phone: String(fd.get("phone") ?? ""),
          topic: String(fd.get("topic") ?? "General inquiry"),
          message,
          acceptTerms: true,
          smsTransactional,
          smsMarketing,
          website: String(fd.get("website") ?? ""),
        },
      });
      setSent(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not send your message. Please try again.");
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <div className="card-surface flex min-h-[420px] flex-col items-center justify-center p-10 text-center">
        <span className="grid h-14 w-14 place-content-center rounded-full bg-primary/10 text-primary">
          <CheckCircle2 className="h-7 w-7" aria-hidden="true" />
        </span>
        <h2 className="mt-5 text-[22px] font-medium tracking-[-0.01em]">Message sent</h2>
        <p className="mt-2 max-w-[380px] text-[14px] leading-relaxed text-muted-foreground">
          Thanks for reaching out. We&apos;ll reply to your email within 1–2 business days. Need
          something urgent? Call{" "}
          <a href={`tel:${PHONE_HREF}`} className="text-foreground underline underline-offset-2">
            {PHONE}
          </a>
          .
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="card-surface p-6 sm:p-8 space-y-6">
      <div className="space-y-1">
        <h2 className="text-[20px] font-medium tracking-[-0.01em]">Send us a message</h2>
        <p className="text-[13px] text-muted-foreground">
          All fields marked <span className="text-destructive">*</span> are required.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field id="firstName" label="First name" required autoComplete="given-name" maxLength={80} />
        <Field id="lastName" label="Last name" required autoComplete="family-name" maxLength={80} />
      </div>
      <Field id="email" label="Email" type="email" required autoComplete="email" maxLength={200} />
      <Field id="phone" label="Phone" type="tel" required autoComplete="tel" maxLength={40} />

      <div className="space-y-1.5">
        <Label htmlFor="topic">What&apos;s this about?</Label>
        <div className="relative">
          <select
            id="topic"
            name="topic"
            defaultValue={TOPICS[0]}
            className="h-10 w-full appearance-none rounded-md border border-input bg-transparent pl-3 pr-9 text-sm shadow-sm transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:border-primary"
          >
            {TOPICS.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <ChevronDown
            className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="message">Message</Label>
        <Textarea
          id="message"
          name="message"
          required
          rows={6}
          minLength={10}
          maxLength={2000}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="resize-y transition-colors hover:border-primary/40 focus-visible:ring-2 focus-visible:ring-ring/40"
          placeholder="Tell us how we can help. Please don't include medical records or other sensitive health information."
        />
        <p className="text-right text-[12px] tabular-nums text-muted-foreground">
          {message.length}
          <span className="opacity-60">/2000</span>
        </p>
      </div>

      {/* ponytail: honeypot — hidden from humans, filled by bots; server drops it silently. */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
      />

      <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
        <Consent checked={acceptTerms} onChange={setAcceptTerms} id="acceptTerms" required>
          I have read and agree to the{" "}
          <Link to="/terms" className="text-foreground underline underline-offset-2">Terms of Service</Link>{" "}
          and{" "}
          <Link to="/privacy" className="text-foreground underline underline-offset-2">Privacy Policy</Link>.
        </Consent>

        <Consent checked={smsTransactional} onChange={setSmsTransactional} id="smsTransactional">
          I agree to receive non-marketing SMS messages from Heallexa about my request, such as
          support or appointment updates.
        </Consent>

        <Consent checked={smsMarketing} onChange={setSmsMarketing} id="smsMarketing">
          I agree to receive marketing SMS messages from Heallexa about new features and offers.
        </Consent>

        <p className="border-t border-border/70 pt-3 text-[12px] leading-relaxed text-muted-foreground">
          By providing your phone number and checking the boxes above, you consent to receive SMS
          messages from Heallexa Inc. Message frequency varies. Msg &amp; data rates may apply. Reply
          STOP to opt out or HELP for help. Consent is not a condition of purchase. See our{" "}
          <Link to="/privacy" className="underline underline-offset-2">Privacy Policy</Link> for how
          we handle your information.
        </p>
      </div>

      <div className="flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
          <Lock className="h-3.5 w-3.5" aria-hidden="true" />
          Sent over an encrypted connection. We never sell your data.
        </p>
        <Button
          type="submit"
          size="lg"
          disabled={sending || !acceptTerms}
          className="sm:min-w-[170px]"
        >
          {sending ? (
            <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
          ) : (
            <><Send className="h-4 w-4" /> Send message</>
          )}
        </Button>
      </div>
    </form>
  );
}

function Field({
  id,
  label,
  required,
  ...props
}: { id: string; label: string } & React.ComponentProps<typeof Input>) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>
      <Input
        id={id}
        name={id}
        required={required}
        className="h-10 transition-colors hover:border-primary/40 focus-visible:ring-2 focus-visible:ring-ring/40"
        {...props}
      />
    </div>
  );
}

function Consent({
  id,
  checked,
  onChange,
  required,
  children,
}: {
  id: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-2.5">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(v) => onChange(v === true)}
        required={required}
        className="mt-0.5"
      />
      <Label htmlFor={id} className="text-[13px] font-normal leading-relaxed text-muted-foreground">
        {children}
        {required && <span className="text-destructive"> *</span>}
      </Label>
    </div>
  );
}
